import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';

const DAMAGE_WAIVER_DOC_ID = 'damageWaiver';

// Types
export interface DamageWaiverSettings {
  id: string;
  defaultAmount: number;
  isPercentage: boolean;
  percentageValue: number;
  fixedAmount: number;
  isTaxable: boolean; // Whether damage waiver fees should be subject to sales tax
  vesselSpecificRates: VesselWaiverRate[];
  uiSettings: {
    headingFontSize: string;
    textFontSize: string;
    primaryColor: string;
    accentColor: string;
    customText: string;
  };
  createdAt: Date;
  updatedAt: Date;
}

export interface VesselWaiverRate {
  id: string;
  vesselType?: string;
  vesselId?: string;
  vesselName?: string;
  isPercentage: boolean;
  percentageValue: number;
  fixedAmount: number;
}

// Initialize default damage waiver settings
const initializeDamageWaiverSettings = async (): Promise<DamageWaiverSettings> => {
  const defaultSettings: DamageWaiverSettings = {
    id: DAMAGE_WAIVER_DOC_ID,
    defaultAmount: 25,
    isPercentage: false,
    percentageValue: 10,
    fixedAmount: 25,
    isTaxable: false, // Default to not taxable
    vesselSpecificRates: [],
    uiSettings: {
      headingFontSize: '1rem',
      textFontSize: '0.875rem',
      primaryColor: '#1976d2',
      accentColor: '#4caf50',
      customText: 'By accepting our damage waiver, you limit your financial responsibility for accidental damage.'
    },
    createdAt: new Date(),
    updatedAt: new Date()
  };

  await setDoc(doc(db, 'settings', DAMAGE_WAIVER_DOC_ID), defaultSettings);
  return defaultSettings;
};

// Get damage waiver settings
export const getDamageWaiverSettings = async (): Promise<DamageWaiverSettings> => {
  const docRef = doc(db, 'settings', DAMAGE_WAIVER_DOC_ID);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      ...data,
      isTaxable: data.isTaxable ?? false, // Ensure isTaxable is never undefined
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date()
    } as DamageWaiverSettings;
  }

  // If settings don't exist, initialize with defaults
  return initializeDamageWaiverSettings();
};

// Update damage waiver settings
export const updateDamageWaiverSettings = async (
  settings: Partial<DamageWaiverSettings>
): Promise<void> => {
  const docRef = doc(db, 'settings', DAMAGE_WAIVER_DOC_ID);
  await updateDoc(docRef, {
    ...settings,
    updatedAt: new Date()
  });
};

// Add a vessel-specific waiver rate
export const addVesselWaiverRate = async (
  rate: Omit<VesselWaiverRate, 'id'>
): Promise<string> => {
  const newRate = {
    ...rate,
    id: Date.now().toString()
  };

  const docRef = doc(db, 'settings', DAMAGE_WAIVER_DOC_ID);
  await updateDoc(docRef, {
    vesselSpecificRates: arrayUnion(newRate),
    updatedAt: new Date()
  });

  return newRate.id;
};

// Update a vessel-specific waiver rate
export const updateVesselWaiverRate = async (
  rate: VesselWaiverRate
): Promise<void> => {
  // First remove the old rate
  const docRef = doc(db, 'settings', DAMAGE_WAIVER_DOC_ID);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const settings = docSnap.data() as DamageWaiverSettings;
    const oldRate = settings.vesselSpecificRates.find(r => r.id === rate.id);
    
    if (oldRate) {
      // Remove old rate and add updated rate
      await updateDoc(docRef, {
        vesselSpecificRates: arrayRemove(oldRate)
      });
      
      await updateDoc(docRef, {
        vesselSpecificRates: arrayUnion(rate),
        updatedAt: new Date()
      });
    }
  }
};

// Remove a vessel-specific waiver rate
export const removeVesselWaiverRate = async (rateId: string): Promise<void> => {
  const docRef = doc(db, 'settings', DAMAGE_WAIVER_DOC_ID);
  const docSnap = await getDoc(docRef);
  
  if (docSnap.exists()) {
    const settings = docSnap.data() as DamageWaiverSettings;
    const rateToRemove = settings.vesselSpecificRates.find(r => r.id === rateId);
    
    if (rateToRemove) {
      await updateDoc(docRef, {
        vesselSpecificRates: arrayRemove(rateToRemove),
        updatedAt: new Date()
      });
    }
  }
};

// Calculate damage waiver amount for a specific vessel
export const calculateDamageWaiverAmount = async (
  vesselId: string, 
  vesselType: string,
  rentalPrice: number
): Promise<number> => {
  const settings = await getDamageWaiverSettings();
  
  // Check for vessel-specific rate first
  const vesselSpecificRate = settings.vesselSpecificRates.find(
    rate => rate.vesselId === vesselId
  );
  
  if (vesselSpecificRate) {
    return vesselSpecificRate.isPercentage 
      ? (rentalPrice * vesselSpecificRate.percentageValue / 100)
      : vesselSpecificRate.fixedAmount;
  }
  
  // Check for vessel-type specific rate
  const typeSpecificRate = settings.vesselSpecificRates.find(
    rate => rate.vesselType === vesselType
  );
  
  if (typeSpecificRate) {
    return typeSpecificRate.isPercentage 
      ? (rentalPrice * typeSpecificRate.percentageValue / 100)
      : typeSpecificRate.fixedAmount;
  }
  
  // Use default rate
  return settings.isPercentage 
    ? (rentalPrice * settings.percentageValue / 100)
    : settings.fixedAmount;
}; 